import { keepPreviousData, type UseQueryOptions, useQuery } from '@tanstack/react-query';
import type { TeamRole } from '@/common/api/types/teamRoles';
import { sortNamesAlphabetically } from '@/common/utils/sorting';
import { capacityQueryKeys } from '@/features/settings/capacity/queryKeys';
import { TeamRolesApi } from '@/features/settings/team-roles/api';

export const getTeamRolesQueryOptions = ({ activeOnly = true }: { activeOnly?: boolean } = {}) => {
  return {
    queryKey: activeOnly
      ? capacityQueryKeys.teamRoles.all()
      : capacityQueryKeys.teamRolesIncludingArchived.all(),
    queryFn: async () => {
      return await TeamRolesApi.getTeamRoles(activeOnly);
    },
    select: sortNamesAlphabetically,
  };
};

export const getSearchTeamRolesQueryOptions = (
  query: string = '',
  { activeOnly = true }: { activeOnly?: boolean } = {},
) => {
  return {
    queryKey: activeOnly
      ? capacityQueryKeys.teamRoles.search(query)
      : capacityQueryKeys.teamRolesIncludingArchived.search(query),
    queryFn: () => TeamRolesApi.searchTeamRoles(query, activeOnly),
    placeholderData: keepPreviousData,
    select: sortNamesAlphabetically,
  };
};

export const useTeamRolesQuery = (
  options: Partial<UseQueryOptions<TeamRole[]>> & { activeOnly?: boolean } = {},
) => {
  const { activeOnly, ...queryOptions } = options;
  return useQuery({ ...getTeamRolesQueryOptions({ activeOnly }), ...queryOptions });
};

export const useSearchTeamRolesQuery = (
  query: string = '',
  { activeOnly }: { activeOnly?: boolean } = {},
) => {
  return useQuery<TeamRole[], Error, TeamRole[]>({
    ...getSearchTeamRolesQueryOptions(query, { activeOnly }),
  });
};
